import {dedupeMachiningById} from "./machiningDedup";

/**
 * Отверстия детали (detailData.holes) для 3D-модуля.
 * Координаты — мм в СК детали: X=length, Y=width, Z=height (толщина).
 * На сцене ModuleDetailMesh: rotateX(−π/2), затем перенос по Z на ширину
 * (+X/+Y/+Z из угла внутрь, как у куба).
 */

const FACE_POSITIONS = new Set(["TOP", "BOTTOM"]);
const SIDE_POSITIONS = new Set(["LEFT", "RIGHT", "FRONT", "BACK"]);

/** Как в Detail.jsx: рисуем только сущности с "hole" в name (имя с бэка). */
export const isRenderableHole = (hole) =>
    Boolean(
        hole
        && String(hole.name || "").toLowerCase().includes("hole")
        && hole.center != null
        && hole.radius != null
        && Number(hole.radius) > 0,
    );

export const getHoleFace = (hole) => {
    const pos = hole?.contour?.position;
    if (pos && SIDE_POSITIONS.has(pos)) return pos;
    if (pos && FACE_POSITIONS.has(pos)) return pos;
    // Нет contour.position — считаем пластью (как старые данные / только center.x/y).
    return "TOP";
};

export const isFaceHole = (hole) => FACE_POSITIONS.has(getHoleFace(hole));

export const isSideHole = (hole) => SIDE_POSITIONS.has(getHoleFace(hole));

/**
 * Сквозное по пласти: depth ≥ толщины (допуск 0.5 мм, как height+5 в модалке).
 * Если depth не задан — считаем сквозным (прежнее поведение ModuleDetailMesh).
 */
export const isThroughFaceHole = (hole, panelHeightMm) => {
    if (!isFaceHole(hole)) return false;
    const depth = Number(hole.depth);
    const height = Number(panelHeightMm);
    if (!Number.isFinite(height) || height <= 0) return true;
    if (!Number.isFinite(depth)) return true;
    return depth >= height - 0.5;
};

export const classifyDetailHoles = (detailData) => {
    const height = Number(detailData?.height) || 18;
    const length = Number(detailData?.length) || 0;
    const width = Number(detailData?.width) || 0;
    const list = dedupeMachiningById((detailData?.holes || []).filter(isRenderableHole));

    const throughFace = [];
    const blindFace = [];
    const side = [];

    for (const hole of list) {
        if (isSideHole(hole)) {
            side.push(hole);
            continue;
        }
        if (isThroughFaceHole(hole, height)) {
            throughFace.push(hole);
        } else {
            blindFace.push(hole);
        }
    }

    return {throughFace, blindFace, side, height, length, width};
};

/** Поза цилиндра глухого отверстия в пласти (локальные мм, ось цилиндра = Z). */
export const getBlindFaceHolePose = (hole, heightMm) => {
    const radius = Number(hole.radius);
    const height = Math.max(Number(heightMm) || 18, 1);
    const depth = Math.min(
        Math.max(Number(hole.depth) || height * 0.5, 0.1),
        height - 0.05,
    );
    const x = Number(hole.center?.x) || 0;
    const y = Number(hole.center?.y) || 0;
    const face = getHoleFace(hole);

    // Extrude: z ∈ [0, height]. BOTTOM — вход с z=0, TOP — с z=height.
    const z = face === "BOTTOM" ? depth / 2 : height - depth / 2;

    return {
        key: hole.id ?? `blind-${x}-${y}-${face}`,
        position: [x, y, z],
        // cylinderGeometry вдоль Y → крутим к оси Z
        rotation: [Math.PI / 2, 0, 0],
        radius,
        depth,
        face,
    };
};

/** Поза цилиндра торцевого отверстия (сверление с ребра внутрь). */
export const getSideHolePose = (hole, lengthMm, widthMm, heightMm) => {
    const radius = Number(hole.radius);
    const length = Math.max(Number(lengthMm) || 0, 1);
    const width = Math.max(Number(widthMm) || 0, 1);
    const height = Math.max(Number(heightMm) || 18, 1);
    const maxDepth = Math.max(length, width, 200);
    const depth = Math.min(Math.max(Number(hole.depth) || 10, 0.1), maxDepth);

    const x = Number(hole.center?.x) || 0;
    const y = Number(hole.center?.y) || 0;
    const z = Number(hole.center?.z) || height / 2;
    const face = getHoleFace(hole);

    switch (face) {
        case "LEFT":
            return {
                key: hole.id ?? `side-L-${y}-${z}`,
                position: [depth / 2, y, z],
                rotation: [0, 0, Math.PI / 2],
                radius,
                depth,
                face,
            };
        case "RIGHT":
            return {
                key: hole.id ?? `side-R-${y}-${z}`,
                position: [length - depth / 2, y, z],
                rotation: [0, 0, Math.PI / 2],
                radius,
                depth,
                face,
            };
        case "FRONT":
            return {
                key: hole.id ?? `side-F-${x}-${z}`,
                position: [x, depth / 2, z],
                rotation: [0, 0, 0],
                radius,
                depth,
                face,
            };
        case "BACK":
            return {
                key: hole.id ?? `side-B-${x}-${z}`,
                position: [x, width - depth / 2, z],
                rotation: [0, 0, 0],
                radius,
                depth,
                face,
            };
        default:
            return null;
    }
};

const HOLE_AXES_OUTSET_MM = 0.4;

/** Ориентация цилиндра оси = как у полости отверстия (ось cylinderGeometry = Y). */
const holeAxisRotation = (face) => {
    switch (face) {
        case "LEFT":
        case "RIGHT":
            return [0, 0, Math.PI / 2];
        case "FRONT":
        case "BACK":
            return [0, 0, 0];
        case "BOTTOM":
        case "TOP":
        default:
            // вдоль Z (толщина), перпендикулярно широкой пласти
            return [Math.PI / 2, 0, 0];
    }
};

/**
 * Центральная ось отверстия у входа (одна линия вдоль сверления),
 * в СК панели: X=length, Y=width, Z=height.
 */
export const getHoleAxesPose = (hole, lengthMm, widthMm, heightMm) => {
    const radius = Number(hole.radius) || 2;
    const length = Math.max(Number(lengthMm) || 0, 1);
    const width = Math.max(Number(widthMm) || 0, 1);
    const height = Math.max(Number(heightMm) || 18, 1);
    const x = Number(hole.center?.x) || 0;
    const y = Number(hole.center?.y) || 0;
    const z = Number(hole.center?.z);
    const midZ = Number.isFinite(z) ? z : height / 2;
    const face = getHoleFace(hole);
    const axisLength = Math.min(28, Math.max(14, radius * 4));
    const rotation = holeAxisRotation(face);

    switch (face) {
        case "BOTTOM":
            return {
                key: hole.id ?? `axes-B-${x}-${y}`,
                position: [x, y, -HOLE_AXES_OUTSET_MM],
                rotation,
                axisLength,
            };
        case "LEFT":
            return {
                key: hole.id ?? `axes-L-${y}-${midZ}`,
                position: [-HOLE_AXES_OUTSET_MM, y, midZ],
                rotation,
                axisLength,
            };
        case "RIGHT":
            return {
                key: hole.id ?? `axes-R-${y}-${midZ}`,
                position: [length + HOLE_AXES_OUTSET_MM, y, midZ],
                rotation,
                axisLength,
            };
        case "FRONT":
            return {
                key: hole.id ?? `axes-F-${x}-${midZ}`,
                position: [x, -HOLE_AXES_OUTSET_MM, midZ],
                rotation,
                axisLength,
            };
        case "BACK":
            return {
                key: hole.id ?? `axes-Bck-${x}-${midZ}`,
                position: [x, width + HOLE_AXES_OUTSET_MM, midZ],
                rotation,
                axisLength,
            };
        case "TOP":
        default:
            return {
                key: hole.id ?? `axes-T-${x}-${y}`,
                position: [x, y, height + HOLE_AXES_OUTSET_MM],
                rotation,
                axisLength,
            };
    }
};
